// react
import { useCallback, useEffect, useMemo } from 'react';

// components
import { Loading as LoadingPage } from '@/components/pages/Loading/Loading';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';

// mui
import { Box } from '@mui/material';

// theme
import { theme } from '@/theme';

interface calendlyCallDetails {
  careCircleMembers?: string[];
  name?: string;
  kindeEmail?: string;
  phoneNumber?: string;
  note?: string;
  reasonForCall?: string;
  accountId?: string;
  careCircleId?: string;
}

interface CalendlyProps {
  baseURL: string;
  isHideGdprBanner?: boolean;
  backgroundColor?: string;
  textColor?: string;
  primaryColor?: string;
  calendlyCallDetails?: calendlyCallDetails;
  isLoading: boolean;
  onBack?: () => void;
}

declare global {
  interface Window {
    Calendly: any;
  }
}

/**
 * Calendly is a component that displays a Hubspot contact form.
 */
export const Calendly = ({
  baseURL,
  backgroundColor,
  isHideGdprBanner = false,
  primaryColor = theme.color.primary[30],
  textColor = theme.color.cards.green.tertiary,
  calendlyCallDetails,
  isLoading,
  onBack,
}: CalendlyProps) => {
  // memos
  const dataURL = useMemo(() => {
    const calendlyURL = new URL(baseURL);

    const queryParams = new URLSearchParams({
      text_color: textColor?.replace('#', '') || '',
      primary_color: primaryColor?.replace('#', '') || '',
    });

    if (calendlyCallDetails) {
      if (calendlyCallDetails.name) {
        queryParams.set('name', calendlyCallDetails.name);
      }
      if (calendlyCallDetails.kindeEmail) {
        queryParams.set('email', calendlyCallDetails.kindeEmail);
      }
      if (calendlyCallDetails.phoneNumber) {
        queryParams.set('a1', `+44${calendlyCallDetails.phoneNumber}`);
      }
      if (calendlyCallDetails.reasonForCall) {
        queryParams.set('a2', calendlyCallDetails.reasonForCall);
      }
      if (calendlyCallDetails.note) {
        queryParams.set('a3', calendlyCallDetails.note);
      }
      if (calendlyCallDetails.careCircleMembers) {
        queryParams.set(
          'guests',
          calendlyCallDetails.careCircleMembers.join(','),
        );
      }
      queryParams.set('utm_source', 'karehero');
      if (calendlyCallDetails.accountId) {
        queryParams.set('utm_content', calendlyCallDetails.accountId);
      }
      if (calendlyCallDetails.careCircleId) {
        queryParams.set('utm_term', calendlyCallDetails.careCircleId);
      }
    }
    if (isHideGdprBanner) queryParams.set('hide_gdpr_banner', '0');
    if (backgroundColor) {
      queryParams.set('background_color', backgroundColor.replace('#', ''));
    }

    calendlyURL.search = queryParams.toString().replace(/\+/g, '%20');
    return calendlyURL.toString();
  }, [
    backgroundColor,
    isHideGdprBanner,
    textColor,
    primaryColor,
    calendlyCallDetails,
  ]);

  // effects
  useEffect(() => {
    if (isLoading) return;

    const calendlyScript = document.createElement('script');
    calendlyScript.src =
      'https://assets.calendly.com/assets/external/widget.js';
    calendlyScript.type = 'text/javascript';
    calendlyScript.async = true;
    document.body.appendChild(calendlyScript);
  }, [isLoading]);

  const isCalendlyEvent = useCallback(
    (e: MessageEvent) =>
      e.origin === 'https://calendly.com' &&
      e.data.event &&
      e.data.event.indexOf('calendly.') === 0,
    [],
  );

  useEffect(() => {
    if (isLoading) return;

    const listener = (e: MessageEvent) => {
      if (!isCalendlyEvent(e)) return;
    };
    window.addEventListener('message', listener);
    return () => {
      window.removeEventListener('message', listener);
    };
  }, [isLoading, isCalendlyEvent]);

  return (
    <ModalPageTemplate onBack={onBack}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
        }}
      >
        {isLoading ? (
          <LoadingPage />
        ) : (
          <Box
            className='calendly-inline-widget'
            data-url={dataURL}
            sx={{ minHeight: 320, flex: 1 }}
          />
        )}
      </Box>
    </ModalPageTemplate>
  );
};

Calendly.displayName = 'Calendly';
export default Calendly;
